import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/core/icon';
import Avatar from '@/core/avatar.js';
import { DropDown, HeaderBookmark } from '@/core/dynamic';
import { getPathByLang, getRootUrl } from '@/helpers/router';
import { ucfirst } from '@/helpers/common';
import SearchComponent from './search.js';
import SearchPageComponent from './searchPageSearch.js';
import Discover from './discovery.js';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import { UseZusModalStore } from '@/helpers/modal.js';
import { useRouter } from 'next/navigation';
import { Search } from '@/core/dynamic';
import ModalWrapper from '@/helpers/modals/skin4.js';
import Image from 'next/image';

// Sous-composant pour le menu utilisateur
const UserMenu = ({ userConnected, disconnect, t, langCurrent }) => (
  <DropDown
    compStyle="z-40 w-42 first:mt-2"
    title={<Avatar className="relative w-10 h-10 overflow-hidden rounded-lg cursor-pointer" user={userConnected} />}
    items={[
      <a className="block px-4 pt-2 pb-3 cursor-pointer text-[15px] text-purple-5/80 hover:text-black hover:bg-purple-0" role="menuitem" key="user_account_v3" href={getPathByLang('user_account_v3', langCurrent)}>{t('header:user.account')}</a>,
      <div
        key="user.logout"
        className="block px-4 pt-2 pb-3 cursor-pointer text-[15px] text-purple-5/80 hover:text-black hover:bg-purple-0"
        role="menuitem"
        onClick={disconnect}
      >
        {t('header:user.logout')}
      </div>,
    ]}
  />
);

// Sous-composant pour le menu d'hébergement
const HostMenu = ({ t, langCurrent }) => (
  <DropDown
    compStyle="z-40 w-42 first:mt-4"
    title={(
      <a className="flex flex-row items-center px-4 py-2 font-light rounded-full cursor-pointer text-purple-4 hover:text-purple-1">
        {t('header:host.host')}
        <Icon name="next" className="w-3 ml-2 transform rotate-90 group-hover:text-white" />
      </a>
    )}
    items={[
      <a href={getPathByLang('dash_host_book', langCurrent)} className="block px-4 pt-3 pb-2 text-[15px] text-purple-5/80 hover:text-black hover:bg-purple-0" role="menuitem">{t('header:booking')}</a>,
      <a key="dash_host_spaces" className="block px-4 py-2 text-[15px] text-purple-5/80 hover:text-black hover:bg-purple-0" href={getPathByLang('dash_host_spaces', langCurrent)} role="menuitem">{t('header:host.listing')}</a>,
      <a key="add_space_v3" href={getPathByLang('add_space_v3', langCurrent)} className="block px-4 py-2 text-[15px] text-purple-5/80 hover:text-black hover:bg-purple-0" role="menuitem">{t('header:host.add_space_menu')}</a>,
      <a href="https://help.wearescene.com/fr/articles/10856826-les-10-cles-du-succes-pour-les-hotes-sur-we-are-scene" target="_blank" className="block px-4 py-2 text-[15px] text-purple-5/80 hover:text-black hover:bg-purple-0" role="menuitem" rel="noreferrer">{t('header:host.host_guide')}</a>,
    ]}
  />
);

const HeaderDesktop = ({ 
  t, 
  langCurrent, 
  isAuthenticated, 
  userConnected, 
  headerBackground, 
  comefrom,
  disconnect,
  openLogin,
  onClickAddSpace,
  getWasUrl,
  dropdown3Ref,
  headerCorpoLinksD
}) => {
  const pageBecomeHost = (comefrom === 'become-host');
  const pageCorpo = (comefrom === 'corpo');
  const isCorpo = userConnected?.corpo?.ref;
  const router = useRouter();
  const [backdropActive, setBackdropActive] = useState(false);
  
  useEffect(() => {
    const checkBackdropActive = () => {
      const hasBackdrop = headerBackground.includes('backdrop-blur');
      setBackdropActive(hasBackdrop);
    };
    
    checkBackdropActive();
  }, [headerBackground]);
  
  useEffect(() => {
    const checkBackdropOnHeader = () => {
      const header = document.querySelector('nav');
      if (header) {
        const hasBlur = header.className.includes('backdrop-blur');
        setBackdropActive(hasBlur || headerBackground.includes('backdrop-blur'));
      }
    };
    
    checkBackdropOnHeader();
    
    const observer = new MutationObserver(checkBackdropOnHeader);
    const header = document.querySelector('nav');
    if (header) {
      observer.observe(header, { attributes: true, attributeFilter: ['class'] });
    }
    
    return () => observer.disconnect();
  }, [headerBackground]);
  
  const openSearch = () => {
    const { setModal1 } = UseZusModalStore.getState();
    setModal1(
      <ModalWrapper onClose={() => setModal1(null)} delay={1}>
        <div className="fixed left-0 w-full top-10">
          <Search isPopin />
        </div>
      </ModalWrapper>,
    );
  };
  return (
    <div className={`mx-auto ${comefrom === 'search' ? 'bg-purple-5 w-full px-5' : 'max-w-[1240px] px-10'}`}>
      <div className="max-w-full mx-auto bg-transparent">
        <div className="relative flex items-center justify-between h-19">
          <div className="flex items-stretch justify-between flex-1">
            <div className="flex items-center">
              <Link 
                aria-label="homepage" 
                className="flex items-center w-[144px] h-[52px]" 
                href={getPathByLang('home', langCurrent)}
              >
                <Image src="/logo-officeriders.svg" alt="Logo" width={144} height={52} className="w-36 h-13 max" priority />
              </Link>
              {backdropActive && comefrom !== 'search' && (
                <button 
                  className="flex items-center px-3 py-2 ml-5 text-sm transition-colors duration-200 rounded-lg gap-x-2 bg-purple/40 hover:bg-purple/80" 
                  onClick={openSearch}
                  aria-label="Rechercher"
                >
                  <div className="size-4"><Icon name="search" className="text-white size-4"/></div>
                  <span className="text-white">{t("header:begin_search")}</span>
                </button>
              )}
              {comefrom === 'search' ? <SearchPageComponent desktop /> : <SearchComponent desktop />}
            </div>
            <div className="flex ml-6">
              <div className="flex items-center">
                {isAuthenticated && !pageCorpo && (
                  <HeaderBookmark className="flex items-center mr-4 text-sm opacity-75 cursor-pointer hover:opacity-100 text-purple-4" book={userConnected.bookmark} />
                )}
                {langCurrent === 'fr' && <a href="tel:+33159134277" className="px-4 py-2 text-sm text-purple-4 hover:text-white">01 59 13 42 77</a>} 
                {pageCorpo || (
                  <div className={`text-base lg:flex flex-grow items-center hidden ${comefrom === 'search' ? 'lg:hidden 2xl:flex' : ''}`}>
                    {langCurrent === 'fr' && <Discover className="px-4 py-2 font-light rounded-full text-purple-4 hover:text-purple-1" />}
                  </div>
                )}
                {isAuthenticated && userConnected?.hasSpaces && !pageCorpo && (
                  <div className="relative items-center flex-grow hidden lg:flex">
                    <HostMenu t={t} langCurrent={langCurrent} />
                  </div>
                )}
                {!isCorpo && (
                  <div className="flex items-center">
                    {!pageBecomeHost && !(isAuthenticated && !userConnected?.hasSpaces && userConnected?.host) && 
                      ((isAuthenticated && userConnected && userConnected.host) || 
                      <Link className="p-3 px-4 py-2 text-base font-light transition-colors duration-300 rounded-lg text-purple-4 hover:text-purple-1 top" href={getPathByLang('become-host', langCurrent)}>{t('header:become_host')}</Link>)}
                  </div>
                )}
                {!isAuthenticated ? <div className="flex items-center gap-x-2">
                  <button className="font-light px-3 py-1.5 text-base rounded-lg cursor-pointer transition-colors duration-300 text-purple-4 hover:text-purple-1" onClick={openLogin}>
                    {t('header:connection')}
                  </button>
                  {pageBecomeHost ? (
                    <a href="#" onClick={() => onClickAddSpace()} className="px-4 py-1.5 mx-2 text-base text-white rounded-lg bg-purple hover:bg-purple-dark hover:text-white">
                      {t('header:host.add_space')}
                    </a>
                  ) : (
                    <button className="font-light px-3 py-1.5 text-base text-purple-1 bg-purple-1/10 border border-purple-4/20 hover:text-purple-1 hover:border-purple-1 rounded-lg cursor-pointer transition-colors duration-300" onClick={() => { useZusUserConnectedStore.setState({ what: 'signup' }); }}>
                      {t('signup:title')}
                    </button>
                  )}
                </div> : <>
                  {userConnected?.hasBookings && <a className="px-4 py-2 font-light rounded-full text-purple-4 hover:text-purple-1" href={getPathByLang('dash_rider_book', langCurrent)}>{t('header:booking')}</a>}
                  {userConnected?.hasGuestBookings && <a className="px-4 py-2 font-light rounded-full text-purple-4 hover:text-purple-1" href={getPathByLang('dash_invited_book', langCurrent)}>{t('header:invitedbooking')}</a>}
                </>}
                {isCorpo && (
                  <div className="static inset-auto z-40 flex items-center pr-0 ml-4 cursor-pointer">
                    <DropDown
                      forwardRef={dropdown3Ref}
                      compStyle="z-40 w-42 first:mt-2"
                      title={(
                        <a className="flex flex-row items-center pr-2 text-base border rounded-lg bg-purple-5/40 hover:bg-purple-6/60 border-purple-4/30 min-h-10">
                          <Avatar className="relative w-8 h-8 m-1 overflow-hidden rounded" renderAvatar user={userConnected} />
                          <p className="ml-2 text-white truncate max-w-40">{ucfirst(userConnected?.corpo?.name)}</p>
                          <Icon name="next" className="w-3 ml-2 text-white transform rotate-90" />
                        </a>)}
                      items={headerCorpoLinksD}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {isAuthenticated && (
            <div className="static inset-auto flex items-center pr-0 ml-4">
              <UserMenu userConnected={userConnected} disconnect={disconnect} t={t} langCurrent={langCurrent} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderDesktop; 